'use client';

import type {
  DatesSetArg,
  DayCellContentArg,
  EventContentArg,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { Stack, Tooltip, Typography } from '@mui/material';
import type { SnapshotStatus } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { DATE_FORMAT, dayjs } from '@/utils/dayjs';

import { PITCalendarWrapper } from './PITCalendarWrapper';
import { SnapshotIndicator } from './SnapshotIndicator';
import { LockIcon } from './lockIcon';
import { OtherSnapshotsMultipleDots } from './otherSnapshotsMultipleDots';
import { PitrSnapshotsMultipleDots } from './pitrSnapshotsMultipleDots';

import { SkullIcon } from '../security/skullIcon';

export interface PointInTimeEvent {
  indicatorColor: string | null;
  vaultName?: string;
  start: Date;
  status?: SnapshotStatus;
  accessDeniedItemsCount?: number;
  isProviderSnapshot?: boolean;
  hasSecurityIssue?: boolean;
  supportPiTR?: boolean;
  protectedFromDeletion?: boolean;
}

interface InternalPointInTimeEvent extends PointInTimeEvent {
  additionalEventsInDate?: number;
}

interface PointsInTimeCalendarProps {
  isLoading?: boolean;
  events: PointInTimeEvent[];
  selectedDate: Date | undefined;
  onDateSelected: (date: Date | undefined) => void;
  onDateRangeChanged: (start: Date, end: Date) => void;
}

export const PointsInTimeCalendar = (props: PointsInTimeCalendarProps) => {
  const [summarizedEvents, setSummarizedEvents] = useState<
    InternalPointInTimeEvent[]
  >([]);
  const [securityIssuesMapper, setSecurityIssuesMapper] =
    useState<Record<string, boolean>>();
  const [protectedSnapshotsMapper, setProtectedSnapshotsMapper] =
    useState<Record<string, boolean>>();

  useEffect(() => {
    const map = new Map<string, InternalPointInTimeEvent[]>();
    const sorted = props.events.sort(
      (a, b) => a.start.getTime() - b.start.getTime()
    );

    const supportPiTRDates = new Set<string>();

    sorted.forEach((event) => {
      const date = dayjs.utc(event.start).format(DATE_FORMAT);

      if (event.supportPiTR && supportPiTRDates.has(date)) {
        return;
      }

      const events = map.get(date) || [];
      if (events.length === 2) {
        events[1].additionalEventsInDate =
          (events[1].additionalEventsInDate || 0) + 1;
      } else {
        events.push(event);
        if (event.supportPiTR) {
          supportPiTRDates.add(date);
        }
      }
      map.set(date, events);
    });

    // setting end event to be on the same day as the start one
    const events = [...map.values()].flat().map((event) => {
      const start = event.start;
      const end = new Date(start);
      end.setSeconds(end.getSeconds() + 1);
      if (start.getDate() !== end.getDate()) {
        start.setSeconds(start.getSeconds() - 1);
        end.setSeconds(end.getSeconds() - 1);
      }
      return {
        ...event,
        start,
        end,
      };
    });

    setSummarizedEvents(events);

    const securityGroupedByDay = props.events.reduce((acc, event) => {
      const date = dayjs.utc(event.start).format(DATE_FORMAT);
      if (event.hasSecurityIssue) {
        acc.set(date, true);
      }
      return acc;
    }, new Map<string, boolean>());

    const protectedGroupedByDay = props.events.reduce((acc, event) => {
      const date = dayjs.utc(event.start).format(DATE_FORMAT);
      if (event.protectedFromDeletion) {
        acc.set(date, true);
      }
      return acc;
    }, new Map<string, boolean>());

    setSecurityIssuesMapper(Object.fromEntries(securityGroupedByDay));
    setProtectedSnapshotsMapper(Object.fromEntries(protectedGroupedByDay));
  }, [props.events]);

  return (
    <PITCalendarWrapper>
      <FullCalendar
        timeZone='UTC'
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView='dayGridMonth'
        weekends={true}
        fixedWeekCount={false}
        showNonCurrentDates={false}
        buttonText={{ today: 'Today' }}
        dayCellContent={(arg: DayCellContentArg) =>
          renderDayCell(arg, securityIssuesMapper, protectedSnapshotsMapper)
        }
        dayCellClassNames={(args) => {
          const classes: string[] = [];
          if (props.isLoading) {
            classes.push('cell-loading');
          }

          const date = dayjs.utc(args.date);
          if (
            props.selectedDate &&
            dayjs.utc(props.selectedDate).isSame(date, 'date')
          ) {
            classes.push('cell-selected');
          }

          if (securityIssuesMapper?.[date.format(DATE_FORMAT)]) {
            classes.push('cell-error');
          }

          if (
            summarizedEvents.every(
              (x) => !dayjs.utc(x.start).isSame(date, 'date')
            )
          ) {
            classes.push('fc-day-disabled');
          }

          return classes;
        }}
        eventClick={(args) => {
          if (args.event.start) {
            props.onDateSelected(args.event.start);
          }
        }}
        dateClick={(args) => {
          const date = dayjs.utc(args.date);
          if (
            summarizedEvents.some((x) =>
              dayjs.utc(x.start).isSame(date, 'date')
            )
          ) {
            props.onDateSelected(args.date);
          }
        }}
        selectable={true}
        dayHeaders={false}
        events={summarizedEvents}
        eventContent={renderEventContent}
        datesSet={(args: DatesSetArg) => {
          props.onDateRangeChanged(args.start, args.end);
        }}
        height='auto'
      />
    </PITCalendarWrapper>
  );
};

const renderEventContent = (args: EventContentArg) => {
  return (
    <Stack direction='row' alignItems='center' data-testid='snapshot-dot'>
      {args.event._def.extendedProps.supportPiTR ? (
        <PitrSnapshotsMultipleDots
          fill={args.event._def.extendedProps.indicatorColor}
        />
      ) : args.event._def.extendedProps.isProviderSnapshot ? (
        <OtherSnapshotsMultipleDots />
      ) : (
        <SnapshotIndicator
          data-testid='snapshot-indicator'
          isPartial={!!args.event._def.extendedProps.accessDeniedItemsCount}
          isPitr={!!args.event._def.extendedProps.supportPiTR}
          backgroundColor={args.event._def.extendedProps.indicatorColor || null}
        />
      )}
      {args.event._def.extendedProps.additionalEventsInDate &&
        args.event._def.extendedProps.additionalEventsInDate > 0 && (
          <span data-testid='snapshot-more-indicator' className='ml-[4px]'>
            +{args.event._def.extendedProps.additionalEventsInDate}
          </span>
        )}
    </Stack>
  );
};

function renderDayCell(
  arg: DayCellContentArg,
  securityIssuesMapper: Record<string, boolean> | undefined,
  protectedSnapshotsMapper: Record<string, boolean> | undefined
) {
  const dateKey = dayjs.utc(arg.date).format(DATE_FORMAT);
  const hasSecurityIssue = securityIssuesMapper?.[dateKey];
  const hasProtectedSnapshot = protectedSnapshotsMapper?.[dateKey];

  return (
    <div>
      <Stack
        className='absolute left-1/2 -translate-x-1/2 bg-[var(--mui-palette-background-paper)]'
        marginTop='-36px'
        direction='row'
        alignItems='center'
      >
        {hasProtectedSnapshot && (
          <Tooltip
            title="The last clean snapshot won't be cleaned by the retention scheduler"
            placement='top'
            arrow
          >
            <LockIcon />
          </Tooltip>
        )}
        {hasSecurityIssue && <SkullIcon />}
      </Stack>
      <Typography>
        {dayjs.utc(arg.date).format('ddd')} {arg.dayNumberText}
      </Typography>
    </div>
  );
}
